import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ALLOWED_FILE_ACCEPT,
  isPreviewableImage,
  validateUploadFile,
} from '../../models/presign-file.model';
import { TemplateModel } from '../../models/template.model';

/** Lo que resuelve el modal al guardar. */
export interface ITemplateModalResult {
  code: string;
  name: string;
  description: string;
  generateQr: boolean;
  status: boolean;
  /** Archivo elegido, o `null` si no se toco la imagen. */
  file: File | null;
  /** MIME del archivo. Va en el body para que el API devuelva el presignado. */
  contentType: string | null;
}

/**
 * Alta y edicion de una plantilla.
 *
 * `code` se captura al crear y se muestra en solo lectura al editar: el API no
 * lo actualiza. La imagen no se sube aqui; el modal solo entrega el `File` y su
 * `contentType`, y la pagina hace el POST/PUT y luego el PUT a la URL firmada.
 */
@Component({
  selector: 'app-template-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './template-modal.component.html',
  styleUrl: './template-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly accept = ALLOWED_FILE_ACCEPT;

  /** `null` = alta. Con modelo = edicion. */
  protected readonly template = signal<TemplateModel | null>(null);

  protected readonly isEdit = computed(() => this.template() !== null);

  protected readonly title = computed(() =>
    this.isEdit() ? 'Editar plantilla' : 'Nueva plantilla',
  );

  protected readonly form = this.formBuilder.nonNullable.group({
    code: [
      '',
      [Validators.required, Validators.maxLength(50), Validators.pattern(/^[A-Za-z0-9_-]+$/)],
    ],
    name: ['', [Validators.required, Validators.maxLength(150)]],
    description: ['', [Validators.maxLength(500)]],
    generateQr: [false],
    // En alta la plantilla nace deshabilitada: el API no recibe `status` y el
    // default de la columna la crea inactiva. En edicion `init()` sobreescribe
    // este valor con el `status` real de la plantilla.
    status: [false],
  });

  protected readonly submitted = signal(false);

  /** Archivo elegido en esta sesion del modal. */
  protected readonly file = signal<File | null>(null);

  /** Data URL de la vista previa. Solo para imagenes; el PDF no se previsualiza. */
  protected readonly preview = signal<string | null>(null);

  /** Error de validacion del archivo (tipo o tamano). */
  protected readonly fileError = signal<string | null>(null);

  /** Imagen que ya tiene la plantilla, cuando no se eligio una nueva. */
  protected readonly currentImage = computed(() => this.template()?.image?.urlFile ?? null);

  /** Carga el modal. `null` abre el formulario en modo alta. */
  init(template: TemplateModel | null): void {
    this.template.set(template);

    if (!template) return;

    this.form.patchValue({
      code: template.code,
      name: template.name,
      description: template.description,
      generateQr: template.generateQr,
      status: template.status,
    });

    // El API no actualiza el codigo: se muestra, pero no se edita.
    this.form.controls.code.disable();
  }

  protected invalid(control: 'code' | 'name' | 'description'): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.touched || this.submitted());
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) return;

    const error = validateUploadFile(file);

    if (error) {
      this.fileError.set(error);
      this.clearFile();
      input.value = '';
      return;
    }

    this.fileError.set(null);
    this.file.set(file);

    if (!isPreviewableImage(file.type)) {
      this.preview.set(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => this.preview.set(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(file);
  }

  protected clearFile(): void {
    this.file.set(null);
    this.preview.set(null);
  }

  protected save(): void {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const current = this.template();
    const file = this.file();

    const result: ITemplateModalResult = {
      code: current ? current.code : value.code.trim(),
      name: value.name.trim(),
      description: value.description.trim(),
      generateQr: value.generateQr,
      status: value.status,
      file,
      contentType: file ? file.type : null,
    };

    this.activeModal.close(result);
  }

  protected cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
